import { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { IonItemSliding, IonItem, IonItemOptions, IonItemOption } from '@ionic/react';
import { Badge, Btn, Card, SectionHeader, DataTable, FormRow, Field, Tabs, FileUpload, Modal, FormActions } from '../../components/ui';
import { Icon } from '../../icons';
import QuotationService from '../../api/quotation';
import PurchaseOrderService from '../../api/purchaseOrder';
import * as vendorService from '../../api/vendors';
import { uploadFiles, downloadFile } from '../../api/files';
import { saveAndOpenFile } from '../../utils/fileUtils';
import { toast } from 'react-hot-toast';
import { getFieldError } from '../../utils/apiUtils';
import { formatDateByPreference } from '../../utils/formatters';
import { useSettings } from '../../context/SettingsContext';
import { useAuth } from '../../context/AuthContext';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';
import QuotationModal from './QuotationModal';

const translations = { en, hi, gu };

const formatGstValue = (val) => {
  if (val === null || val === undefined) return "";
  const str = String(val).trim();
  if (!str) return "";
  const clean = str.replace(/%/g, "").trim();
  if (!clean) return "";
  return `${clean}%`;
};

export default function QuotationsScreen() {
  const { language = 'en' } = useSettings();
  const { user } = useAuth();
  const t = (key, params) => {
    let str = translations[language]?.quotations?.[key] || translations.en?.quotations?.[key] || key;
    if (params) { Object.keys(params).forEach(p => { str = str.replace(`{${p}}`, params[p]); }); }
    return str;
  };
  const [tab, setTab] = useState(0);
  const [modal, setModal] = useState(null);
  const [docsModal, setDocsModal] = useState(null);
  const [editQuotationData, setEditQuotationData] = useState(null);
  const [poForm, setPoForm] = useState({ quotation_id: "", vendor_id: "", custom_vendor: "", amount: "", issue_date: "2026-04-12", delivery_date: "", status: "Draft", files: [] });
  const openNewQuotationModal = () => {
    setEditQuotationData(null);
    setModal("quotation");
  };
  const pp = k => e => setPoForm(f => ({ ...f, [k]: typeof e === "object" && e.target ? e.target.value : e }));
  const [quotations, setQuotations] = useState([]);
  const [pos, setPos] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [editPoId, setEditPoId] = useState(null);
  const fetched = useRef(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const activeQuotation = useMemo(() => {
    if (!selectedQuotation) return null;
    return quotations.find(q => String(q.id) === String(selectedQuotation.id)) || selectedQuotation;
  }, [quotations, selectedQuotation]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [historyModal, setHistoryModal] = useState(null);
  const [workflowModal, setWorkflowModal] = useState(null);
  
  // Table Enhancements State
  const [qSearch, setQSearch] = useState("");
  const [qFilters, setQFilters] = useState({ status: "All", requester: "All", vendor: "All", date: "" });
  const [qSort, setQSort] = useState({ key: "created_at", direction: "desc" });
  const [qPage, setQPage] = useState(1);
  const qPerPage = 10;
  const [poFilters, setPoFilters] = useState({ status: "All" });
  
  // Reports Filters
  const [reportQStatus, setReportQStatus] = useState("All");
  const [reportPOStatus, setReportPOStatus] = useState("All");

  const fetchData = async () => {
    setLoading(true);
    try {
      const [qRes, poRes, vRes] = await Promise.all([
        QuotationService.getQuotations(),
        PurchaseOrderService.getPurchaseOrders(),
        vendorService.getVendors()
      ]);
      setQuotations(Array.isArray(qRes) ? qRes : qRes?.data || []);
      setPos(Array.isArray(poRes) ? poRes : poRes?.data || []);
      setVendors(Array.isArray(vRes) ? vRes : vRes?.data || []);
    } catch (err) {
      toast.error(t("toast_load_data_failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    fetchData();
  }, []);

  const availableRequesters = useMemo(() => ["All", ...new Set(quotations.map(q => q.requested_by).filter(Boolean))], [quotations]);
  const availableVendors = useMemo(() => ["All", ...new Set(quotations.flatMap(q => (q.bids || []).map(b => b.vendor_name)).filter(Boolean))], [quotations]);

  const filteredQuotations = useMemo(() => {
    let result = [...quotations];
    
    if (qSearch) {
      const s = qSearch.toLowerCase();
      result = result.filter(q => 
        q.title?.toLowerCase().includes(s) || 
        String(q.id).includes(s) ||
        (q.bids || []).some(b => b.vendor_name?.toLowerCase().includes(s))
      );
    }
    
    if (qFilters.status && qFilters.status !== "All") {
      result = result.filter(q => q.status === qFilters.status);
    }
    if (qFilters.requester && qFilters.requester !== "All") {
      result = result.filter(q => q.requested_by === qFilters.requester);
    }
    if (qFilters.vendor && qFilters.vendor !== "All") {
      result = result.filter(q => (q.bids || []).some(b => b.vendor_name === qFilters.vendor));
    }
    if (qFilters.date) {
      result = result.filter(q => q.created_at && q.created_at.startsWith(qFilters.date));
    }
    
    result.sort((a, b) => {
      let aVal, bVal;
      switch(qSort.key) {
        case 'created_at': aVal = new Date(a.created_at || 0).getTime(); bVal = new Date(b.created_at || 0).getTime(); break;
        case 'vendor_count': aVal = (a.bids || []).length; bVal = (b.bids || []).length; break;
        case 'lowest_bid': 
          const aBids = (a.bids || []).sort((x, y) => parseFloat(x.amount) - parseFloat(y.amount));
          const bBids = (b.bids || []).sort((x, y) => parseFloat(x.amount) - parseFloat(y.amount));
          aVal = aBids.length > 0 ? parseFloat(aBids[0].amount) : Infinity;
          bVal = bBids.length > 0 ? parseFloat(bBids[0].amount) : Infinity;
          break;
        case 'status': aVal = a.status; bVal = b.status; break;
        default: aVal = 0; bVal = 0;
      }
      if (aVal < bVal) return qSort.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return qSort.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [quotations, qSearch, qFilters, qSort]);
  
  const totalQPages = Math.ceil(filteredQuotations.length / qPerPage) || 1;
  const paginatedQuotations = filteredQuotations.slice((qPage - 1) * qPerPage, qPage * qPerPage);

  useEffect(() => { setQPage(1); }, [qSearch, qFilters, qSort]);


  const submitWorkflow = async () => {
    if (!workflowModal) return;
    setSaving(true);
    try {
      const payload = {
        status: workflowModal.type === 'Approve' ? 'Approved' : (workflowModal.type === 'Reject' ? 'Rejected' : 'Cancelled'),
        approval_comments: workflowModal.comments
      };
      const updatedData = await QuotationService.updateQuotation(workflowModal.quotation.id, payload);
      const updatedQuotation = updatedData.data || updatedData;
      setQuotations(prev => prev.map(q => String(q.id) === String(workflowModal.quotation.id) ? updatedQuotation : q));
      setSelectedQuotation(updatedQuotation);
      
      toast.success(`Request ${workflowModal.type}d successfully`);
      
      const v = workflowModal.vendor;
      const type = workflowModal.type;
      setWorkflowModal(null);
      
      if (type === 'Approve' && v) {
        setPoForm(f => ({ ...f, quotation_id: updatedQuotation.id, vendor_id: v.vendor_name, amount: v.amount }));
        setModal("po");
      } else {
        setTab(0);
      }
    } catch (err) {
      toast.error(getFieldError(err) || "Failed to update workflow");
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (q) => {
    setEditQuotationData(q);
    setModal("quotation");
  };

  const deleteQuotation = async (id) => {
    if (!window.confirm(t("confirm_delete_quotation"))) return;
    try {
      await QuotationService.deleteQuotation(id);
      setQuotations(prev => prev.filter(q => q.id !== id));
      toast.success(t("toast_delete_quotation_success"));
    } catch (err) {
      toast.error(t("toast_delete_quotation_failed"));
    }
  };

  const submitPO = async () => {
    const errors = {};
    if (!poForm.amount) errors.amount = "Required";
    if (!poForm.vendor_id) {
      errors.vendor_id = "Required";
    } else if (poForm.vendor_id === "__custom__") {
      const cv = (poForm.custom_vendor || "").trim();
      if (!cv) {
        errors.custom_vendor = "Required";
      } else if (!/\p{L}/u.test(cv)) {
        errors.custom_vendor = t("val_vendor_name_letters", "Vendor name cannot be numeric only");
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      if (errors.custom_vendor && errors.custom_vendor !== "Required") {
        toast.error(errors.custom_vendor);
      } else {
        toast.error(t("toast_vendor_amount_required"));
      }
      return;
    }
    setSaving(true);
    setFormErrors({});
    try {
      let poFileIds = [];
      const existingFiles = (poForm.files || []).filter(f => !(f instanceof File) && f.id);
      poFileIds = existingFiles.map(f => f.id);

      const newFiles = (poForm.files || []).filter(f => f instanceof File);
      if (newFiles && newFiles.length > 0) {
        const uploadRes = await uploadFiles(newFiles, 'purchase_orders');
        const uploaded = uploadRes.data?.data?.uploaded || uploadRes.data?.uploaded || uploadRes.uploaded || [];
        poFileIds = [...poFileIds, ...uploaded.map(f => f.id)];
      }

      const payload = {
        quotation_id: poForm.quotation_id ? parseInt(poForm.quotation_id) : null,
        vendor_id: poForm.vendor_id === "__custom__" ? poForm.custom_vendor : poForm.vendor_id,
        amount: poForm.amount,
        status: poForm.status,
        issue_date: poForm.issue_date,
        delivery_date: poForm.delivery_date,
        file_ids: poFileIds
      };

      if (editPoId) {
        const updatedData = await PurchaseOrderService.updatePurchaseOrder(editPoId, payload);
        const updatedItem = updatedData.data || updatedData;
        setPos(prev => prev.map(p => p.id === editPoId ? updatedItem : p));
        toast.success(t("toast_update_po_success"));
      } else {
        const createdData = await PurchaseOrderService.createPurchaseOrder(payload);
        const createdItem = createdData.data || createdData;
        setPos(prev => [createdItem, ...prev]);
        toast.success(t("toast_create_po_success"));
      }

      if (poForm.quotation_id && !editPoId) {
        setQuotations(prev => prev.map(q => q.id === parseInt(poForm.quotation_id) ? { ...q, status: 'Approved' } : q));
        setSelectedQuotation(null);
        setTab(2);
      }

      setModal(null);
      setEditPoId(null);
      setPoForm({ quotation_id: "", vendor_id: "", custom_vendor: "", amount: "", issue_date: "2026-04-12", delivery_date: "", status: "Draft", files: [] });
    } catch (err) {
      if (err.response?.data) {
        setFormErrors(err.response.data);
      }
      toast.error(err.response?.data?.message || (editPoId ? t("toast_update_po_failed") : t("toast_create_po_failed")));
    } finally {
      setSaving(false);
    }
  };

  const openEditPO = (po) => {
    setEditPoId(po.id);
    setPoForm({
      quotation_id: po.quotation_id || "",
      vendor_id: po.vendor_name || po.vendor_id || "",
      custom_vendor: "",
      amount: po.amount,
      status: po.status || "Draft",
      issue_date: po.issue_date ? po.issue_date.split('T')[0] : "",
      delivery_date: po.delivery_date ? po.delivery_date.split('T')[0] : "",
      files: po.attachments || []
    });
    setModal("po");
  };

  const deletePO = async (id) => {
    if (!window.confirm(t("confirm_delete_po"))) return;
    try {
      await PurchaseOrderService.deletePurchaseOrder(id);
      setPos(prev => prev.filter(p => p.id !== id));
      toast.success(t("toast_delete_po_success"));
    } catch (err) {
      toast.error(t("toast_delete_po_failed"));
    }
  };

  const handleDownloadPdf = async (po) => {
    try {
      const response = await PurchaseOrderService.downloadPdf(po.id);
      // For axios responseType: blob, data is in response or response.data depending on setup
      const blob = response instanceof Blob ? response : response.data;
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PO_${po.po_number || po.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t("toast_download_success", "Downloaded successfully!"));
    } catch (err) {
      toast.error(t("toast_download_failed", "Failed to download PDF"));
    }
  };

  const handleExportQuotations = async (status = 'All') => {
    try {
      const response = await QuotationService.exportQuotations(status);
      const blob = response instanceof Blob ? response : response.data;
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quotations_report_${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Quotations exported successfully");
    } catch (err) {
      toast.error("Failed to export quotations");
    }
  };

  const handleExportPO = async (status = 'All') => {
    try {
      const response = await PurchaseOrderService.exportPurchaseOrders(status);
      const blob = response instanceof Blob ? response : response.data;
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `purchase_orders_report_${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Purchase Orders exported successfully");
    } catch (err) {
      toast.error("Failed to export purchase orders");
    }
  };

  const handleExportComparison = async (id = activeQuotation?.id) => {
    const targetId = id || activeQuotation?.id;
    if (!targetId) return;

    if (activeQuotation?.id === targetId && (!activeQuotation?.bids || activeQuotation.bids.length === 0)) {
      toast.error(t("msg_no_bids", "No bids found to compare."));
      return;
    }

    try {
      const response = await QuotationService.exportVendorComparison(targetId);
      const blob = response instanceof Blob
        ? response
        : new Blob([response?.data || response], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          });
      await saveAndOpenFile(blob, `vendor_comparison_${targetId}.xlsx`);
      toast.success("Comparison exported successfully");
    } catch (err) {
      console.error("Export comparison error:", err);
      toast.error(err?.message || "Failed to export vendor comparison");
    }
  };

  const handleExportAuditHistory = async () => {
    try {
      const response = await QuotationService.exportAuditHistory();
      const blob = response instanceof Blob ? response : response.data;
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_history_report_${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Audit History exported successfully");
    } catch (err) {
      toast.error("Failed to export Audit History");
    }
  };

  return (
    <div className="fade-up space-y-4">
      {modal === "quotation" && (
        <QuotationModal
          isOpen={modal === "quotation"}
          onClose={() => {
            setModal(null);
            setEditQuotationData(null);
          }}
          editQuotation={editQuotationData}
          onSaved={(item, isEdit) => {
            if (isEdit) {
              setQuotations(prev => prev.map(q => String(q.id) === String(item.id) ? item : q));
            } else {
              setQuotations(prev => [item, ...prev]);
            }
            setSelectedQuotation(item);
          }}
          user={user}
          t={t}
        />
      )}
      {modal === "po" && (
        <Modal title={editPoId ? t("title_edit_po") : t("title_create_po")} onClose={() => { setModal(null); setEditPoId(null); setFormErrors({}); }}>
          <FormRow cols={2}>
            <Field
              label={t("label_against_quotation")}
              type="select"
              value={poForm.quotation_id}
              onChange={pp("quotation_id")}
              options={[
                { value: "", label: t("opt_direct_purchase") },
                ...quotations.map(q => ({ value: q.id, label: q.title }))
              ]}
              error={getFieldError(formErrors, 'quotation_id')}
            />
            <Field
              label={t("label_vendor")}
              type="select"
              value={poForm.vendor_id}
              onChange={pp("vendor_id")}
              options={[
                { value: "", label: t("opt_select_vendor") },
                ...vendors.map(v => ({ value: v.name, label: v.name })),
                ...(poForm.quotation_id 
                  ? (quotations.find(q => q.id === parseInt(poForm.quotation_id))?.bids || [])
                      .filter(b => !vendors.some(v => v.name === b.vendor_name))
                      .map(b => ({ value: b.vendor_name, label: `${b.vendor_name} (Bid)` }))
                  : []),
                { value: "__custom__", label: "+ Add Custom Vendor" }
              ]}
              required
              error={getFieldError(formErrors, 'vendor_id')}
              disabled={!!poForm.quotation_id}
            />
          </FormRow>
          {poForm.vendor_id === "__custom__" && (
            <FormRow>
              <Field
                label="Custom Vendor Name"
                value={poForm.custom_vendor || ""}
                onChange={e => {
                  const val = e.target.value;
                  setPoForm(f => ({ ...f, custom_vendor: val }));
                  if (formErrors.custom_vendor) {
                    const trimmed = val.trim();
                    if (!trimmed) {
                      setFormErrors(prev => ({ ...prev, custom_vendor: "Required" }));
                    } else if (!/\p{L}/u.test(trimmed)) {
                      setFormErrors(prev => ({ ...prev, custom_vendor: t("val_vendor_name_letters", "Vendor name cannot be numeric only") }));
                    } else {
                      setFormErrors(prev => {
                        const { custom_vendor, ...rest } = prev;
                        return rest;
                      });
                    }
                  }
                }}
                onBlur={e => {
                  const trimmed = e.target.value.trim();
                  if (trimmed && !/\p{L}/u.test(trimmed)) {
                    setFormErrors(prev => ({ ...prev, custom_vendor: t("val_vendor_name_letters", "Vendor name cannot be numeric only") }));
                  }
                }}
                placeholder="Enter vendor name..."
                required
                error={formErrors.custom_vendor}
              />
            </FormRow>
          )}
          <FormRow cols={2}>
            <Field label={t("label_amount_rs")} type="number" value={poForm.amount} onChange={pp("amount")} required error={getFieldError(formErrors, 'amount')} />
            <Field label={t("label_status")} type="select" value={poForm.status} onChange={pp("status")} options={["Draft", "Sent", "Approved", "Completed", "Cancelled"]} />
          </FormRow>
          <FormRow cols={2}>
            <Field label={t("label_issue_date")} type="date" value={poForm.issue_date} onChange={pp("issue_date")} />
            <Field label={t("label_expected_delivery")} type="date" value={poForm.delivery_date} onChange={pp("delivery_date")} />
          </FormRow>
          <FileUpload label={t("label_attach_po")} accept="image/*,.pdf" files={poForm.files} onChange={files => setPoForm(f => ({ ...f, files }))} />
          <FormActions className="pb-10 sm:pb-0" onSave={submitPO} onCancel={() => { setModal(null); setEditPoId(null); setFormErrors({}); }} label={editPoId ? t("btn_update_po") : t("btn_create_po")} loading={saving} />
        </Modal>
      )}
      {selectedPO && (
        <Modal title={t("title_purchase_order_id", { number: selectedPO.po_number })} onClose={() => setSelectedPO(null)}>
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4 bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl">
              <div>
                <div className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-1">{t("label_vendor")}</div>
                <div className="font-semibold text-ink">{selectedPO.vendor_name}</div>
              </div>
              <div>
                <div className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-1">{t("placeholder_amount")}</div>
                <div className="font-semibold text-ink">₹{parseFloat(selectedPO.amount).toLocaleString('en-IN')}</div>
              </div>
              <div>
                <div className="text-sm font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">{t("label_issue_date")}</div>
                <div className="font-medium text-slate-700 dark:text-slate-300">{selectedPO.issue_date ? formatDateByPreference(selectedPO.issue_date) : '-'}</div>
              </div>
              <div>
                <div className="text-sm font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">{t("label_expected_delivery")}</div>
                <div className="font-medium text-slate-700 dark:text-slate-300">{selectedPO.delivery_date ? formatDateByPreference(selectedPO.delivery_date) : '-'}</div>
              </div>
            </div>

            <div>
              <div className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">{t("label_status")}</div>
              <Badge color={selectedPO.status === 'Completed' ? 'green' : selectedPO.status === 'Approved' ? 'blue' : selectedPO.status === 'Cancelled' ? 'red' : 'amber'} dot>
                {selectedPO.status}
              </Badge>
            </div>

            {selectedPO.quotation && (
              <div>
                <div className="text-sm font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2">{t("label_against_quotation")}</div>
                <div className="text-sm border border-slate-200 dark:border-slate-800 rounded-lg p-3">
                  <div className="font-semibold text-ink">{selectedPO.quotation.title}</div>
                  <div className="text-muted text-xs mt-1">{t("label_requested_by")}: {selectedPO.quotation.requested_by}</div>
                </div>
              </div>
            )}

            <div>
              <div className="text-sm font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2">
                {t("label_attached_files", "Attached Files")}{selectedPO.attachments?.length ? ` (${selectedPO.attachments.length})` : ''}
              </div>
              {(!selectedPO.attachments || selectedPO.attachments.length === 0) ? (
                <div className="text-xs text-muted italic bg-slate-50 dark:bg-slate-800/40 p-3 rounded-xl border border-dashed border-slate-200 dark:border-slate-800">
                  {t("msg_no_attachments_po", "No attachments attached to this purchase order.")}
                </div>
              ) : (
                <div className="space-y-2">
                  {selectedPO.attachments.map((file, i) => {
                    const isPdf = file.extension === 'pdf' || (file.mime_type && file.mime_type.includes('pdf'));
                    const sizeStr = file.size ? `${(file.size / 1024).toFixed(1)} KB` : '';
                    return (
                      <div
                        key={file.id || i}
                        className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl"
                      >
                        <div className="flex items-center gap-3 min-w-0 pr-2">
                          <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-primary/20 border border-blue-100 dark:border-primary/30 flex items-center justify-center text-primary flex-shrink-0">
                            <Icon name={isPdf ? 'documents' : 'eye'} size={16} />
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-ink dark:text-slate-200 truncate" title={file.original_name}>
                              {file.original_name}
                            </div>
                            <div className="text-xs text-muted">
                              {sizeStr && <span>{sizeStr}</span>}
                              {file.extension && <span>{sizeStr ? ' · ' : ''}{file.extension.toUpperCase()}</span>}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const url = file.temporary_url || file.file_url;
                            if (url) {
                              window.open(url, '_blank');
                            } else {
                              downloadFile(file.id, file.original_name);
                            }
                          }}
                          className="px-2.5 py-1 bg-primary hover:bg-primary/90 text-white text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer shadow-sm flex-shrink-0"
                        >
                          <Icon name="download" size={12} />
                          <span>{t("btn_view", "View")}</span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <div className="mt-6 flex justify-end">
            <Btn onClick={() => setSelectedPO(null)}>{t("btn_close")}</Btn>
          </div>
        </Modal>
      )}
      {docsModal && (
        <Modal
          title={`${t("label_supporting_documents", "Supporting Documents")} - ${docsModal.title || `REQ-${String(docsModal.id).padStart(4, '0')}`}`}
          onClose={() => setDocsModal(null)}
        >
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-border flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">{t("label_title", "Title")}</span>
                <span className="font-semibold text-ink dark:text-slate-200">{docsModal.title}</span>
              </div>
              <div>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">{t("label_requested_by", "Requested By")}</span>
                <span className="text-sm text-ink dark:text-slate-300">{docsModal.requested_by || "-"}</span>
              </div>
            </div>

            {(!docsModal.attachments || docsModal.attachments.length === 0) ? (
              <div className="py-12 text-center flex flex-col items-center justify-center">
                <div className="w-14 h-14 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 mb-3">
                  <Icon name="documents" size={28} />
                </div>
                <div className="text-sm font-bold text-ink dark:text-slate-200 mb-1">{t("title_no_supporting_docs", "No Supporting Documents")}</div>
                <p className="text-xs text-muted max-w-sm">
                  {t("msg_no_supporting_docs", "No supporting documents were attached to this quotation request.")}
                </p>
                {['Pending', 'Rejected'].includes(docsModal.status) && (
                  <Btn xs className="mt-4" icon="plus" onClick={() => { const q = docsModal; setDocsModal(null); openEdit(q); }}>
                    {t("btn_upload_docs", "Upload Documents")}
                  </Btn>
                )}
              </div>
            ) : (
              <div className="space-y-2.5">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block">
                  {t("label_attached_files", "Attached Files")} ({docsModal.attachments.length})
                </span>
                {docsModal.attachments.map((file, i) => {
                  const isPdf = file.extension === 'pdf' || (file.mime_type && file.mime_type.includes('pdf'));
                  const sizeStr = file.size ? `${(file.size / 1024).toFixed(1)} KB` : '';
                  return (
                    <div
                      key={file.id || i}
                      className="flex items-center justify-between p-3.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl hover:border-primary/30 transition-all"
                    >
                      <div className="flex items-center gap-3 min-w-0 pr-2">
                        <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-primary/20 border border-blue-100 dark:border-primary/30 flex items-center justify-center text-primary flex-shrink-0">
                          <Icon name={isPdf ? 'documents' : 'eye'} size={18} />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-ink dark:text-slate-200 truncate" title={file.original_name}>
                            {file.original_name}
                          </div>
                          <div className="text-xs text-muted mt-0.5 flex items-center gap-1.5">
                            {sizeStr && <span>{sizeStr}</span>}
                            {file.extension && <span>· {file.extension.toUpperCase()}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => {
                            const url = file.temporary_url || file.file_url;
                            if (url) {
                              window.open(url, '_blank');
                            } else {
                              downloadFile(file.id, file.original_name);
                            }
                          }}
                          className="px-3 py-1.5 bg-primary hover:bg-primary/90 text-white text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer shadow-sm"
                        >
                          <Icon name="eye" size={14} />
                          <span>{t("btn_view", "View")}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => downloadFile(file.id, file.original_name)}
                          className="px-2.5 py-1.5 bg-white dark:bg-slate-700 hover:bg-slate-100 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-xs font-semibold rounded-lg border border-slate-200 dark:border-slate-600 transition-all flex items-center gap-1 cursor-pointer"
                          title="Download"
                        >
                          <Icon name="download" size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="pt-3 border-t border-border flex justify-end">
              <Btn sm onClick={() => setDocsModal(null)}>{t("btn_close", "Close")}</Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Dashboard Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-border shadow-sm flex flex-col relative overflow-hidden group">
          <div className="absolute -right-4 -top-4 w-16 h-16 bg-blue-50 dark:bg-blue-900/10 rounded-full opacity-50 group-hover:scale-150 transition-transform duration-500"></div>
          <div className="flex items-center gap-2 mb-1 z-10">
            <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 flex items-center justify-center">
              <Icon name="documents" size={16} />
            </div>
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{t("label_total_quotations")}</span>
          </div>
          <span className="text-2xl font-black text-ink mt-1 ml-10 z-10">{quotations.length}</span>
        </div>
        
        <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-border shadow-sm flex flex-col relative overflow-hidden group">
          <div className="absolute -right-4 -top-4 w-16 h-16 bg-amber-50 dark:bg-amber-900/10 rounded-full opacity-50 group-hover:scale-150 transition-transform duration-500"></div>
          <div className="flex items-center gap-2 mb-1 z-10">
            <div className="w-8 h-8 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 flex items-center justify-center">
              <Icon name="clock" size={16} />
            </div>
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{t("label_active_rfqs")}</span>
          </div>
          <span className="text-2xl font-black text-amber-600 dark:text-amber-500 mt-1 ml-10 z-10">{quotations.filter(q => q.status === 'Pending').length}</span>
        </div>
        
        <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-border shadow-sm flex flex-col relative overflow-hidden group">
          <div className="absolute -right-4 -top-4 w-16 h-16 bg-green-50 dark:bg-green-900/10 rounded-full opacity-50 group-hover:scale-150 transition-transform duration-500"></div>
          <div className="flex items-center gap-2 mb-1 z-10">
            <div className="w-8 h-8 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 flex items-center justify-center">
              <Icon name="check" size={16} />
            </div>
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{t("label_completed_requests")}</span>
          </div>
          <span className="text-2xl font-black text-green-600 dark:text-green-500 mt-1 ml-10 z-10">{quotations.filter(q => q.status === 'Approved').length}</span>
        </div>
        
        <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-border shadow-sm flex flex-col relative overflow-hidden group">
          <div className="absolute -right-4 -top-4 w-16 h-16 bg-indigo-50 dark:bg-indigo-900/10 rounded-full opacity-50 group-hover:scale-150 transition-transform duration-500"></div>
          <div className="flex items-center gap-2 mb-1 z-10">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold uppercase leading-none">PO</span>
            </div>
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{t("label_total_pos")}</span>
          </div>
          <span className="text-2xl font-black text-indigo-600 dark:text-indigo-400 mt-1 ml-10 z-10">{pos.length}</span>
        </div>
      </div>

      <Tabs tabs={[t("tab_all_quotations"), t("tab_compare_bids"), t("tab_purchase_orders"), t("tab_reports")]} active={tab} onChange={setTab} />
      {tab === 0 && <Card>
        <SectionHeader title={t("title_quotation_requests")} action={<div className="hidden lg:flex items-center gap-2"><Btn sm outline onClick={() => handleExportQuotations(qFilters.status)} icon="download">Export Excel</Btn><Btn sm variant="primary" icon="plus" onClick={openNewQuotationModal}>{t("btn_new_request")}</Btn></div>} />
        {loading ? <div className="py-12 text-center text-muted">{t("msg_loading")}</div> : (
          <>
            <div className="mb-4 flex flex-col gap-3">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <input type="text" placeholder="Search by Title, Vendor, or ID..." value={qSearch} onChange={e => setQSearch(e.target.value)} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-slate-50 dark:bg-slate-800/50 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/20" />
                </div>
                <div className="flex flex-wrap sm:flex-nowrap gap-2">
                  <select value={qFilters.status} onChange={e => setQFilters(f => ({ ...f, status: e.target.value }))} className="px-3 py-2 border border-border rounded-lg text-sm bg-slate-50 dark:bg-slate-800/50 text-slate-800 dark:text-slate-100 outline-none">
                    <option className="dark:bg-slate-800" value="All">All Statuses</option>
                    <option className="dark:bg-slate-800" value="Pending">Pending</option>
                    <option className="dark:bg-slate-800" value="Approved">Approved</option>
                    <option className="dark:bg-slate-800" value="Rejected">Rejected</option>
                  </select>
                  <select value={qFilters.requester} onChange={e => setQFilters(f => ({ ...f, requester: e.target.value }))} className="px-3 py-2 border border-border rounded-lg text-sm bg-slate-50 dark:bg-slate-800/50 text-slate-800 dark:text-slate-100 outline-none max-w-[150px]">
                    {availableRequesters.map(r => <option className="dark:bg-slate-800" key={r} value={r}>{r === "All" ? "All Requesters" : r}</option>)}
                  </select>
                  <select value={qFilters.vendor} onChange={e => setQFilters(f => ({ ...f, vendor: e.target.value }))} className="px-3 py-2 border border-border rounded-lg text-sm bg-slate-50 dark:bg-slate-800/50 text-slate-800 dark:text-slate-100 outline-none max-w-[150px]">
                    {availableVendors.map(v => <option className="dark:bg-slate-800" key={v} value={v}>{v === "All" ? "All Vendors" : v}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted">
                <span>Sort by:</span>
                <select value={qSort.key} onChange={e => setQSort(s => ({ ...s, key: e.target.value }))} className="px-2 py-1 border border-border rounded text-sm bg-slate-50 dark:bg-slate-800/50 text-slate-800 dark:text-slate-100 font-semibold outline-none cursor-pointer">
                  <option className="dark:bg-slate-800" value="created_at">Date</option>
                  <option className="dark:bg-slate-800" value="vendor_count">Vendor Count</option>
                  <option className="dark:bg-slate-800" value="lowest_bid">Lowest Bid</option>
                  <option className="dark:bg-slate-800" value="status">Status</option>
                </select>
                <button onClick={() => setQSort(s => ({ ...s, direction: s.direction === 'asc' ? 'desc' : 'asc' }))} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded">
                  <Icon name={qSort.direction === 'asc' ? 'arrow_up' : 'arrow_down'} size={14} />
                </button>
              </div>
            </div>
            <div className="hidden lg:block">
              <DataTable cols={["Request ID", "Created", t("label_title"), t("label_requested_by"), "Due Date", "Vendor Responses", "Selected Vendor", t("col_lowest_bid"), t("label_status"), t("col_actions")]} rows={paginatedQuotations.map(q => {
                const sortedBids = q.bids ? [...q.bids].sort((a, b) => parseFloat(a.amount) - parseFloat(b.amount)) : [];
                const lowestBid = sortedBids.length > 0 ? `₹${parseFloat(sortedBids[0].amount).toLocaleString('en-IN')}` : "-";

                let badgeColor = "amber";
                if (q.status === 'Approved') badgeColor = "green";
                else if (q.status === 'Rejected') badgeColor = "red";
                else if (q.status === 'Cancelled') badgeColor = "slate";

                let selectedVendor = "-";
                if (q.status === 'Approved' && q.purchase_orders && q.purchase_orders.length > 0) {
                  selectedVendor = q.purchase_orders[0].vendor_name;
                }

                return [
                  <span className="font-mono font-medium text-slate-500">REQ-{String(q.id).padStart(4, '0')}</span>,
                  q.created_at ? formatDateByPreference(q.created_at) : "-",
                  <strong>{q.title}</strong>,
                  q.requested_by || "-",
                  q.due_date ? formatDateByPreference(q.due_date) : "-",
                  <span className="text-sm">{(q.bids || []).length} / {q.total_vendors_invited || 1}</span>,
                  <span className="font-semibold text-primary">{selectedVendor}</span>,
                  lowestBid,
                  <Badge color={badgeColor} dot>{q.status}</Badge>,
                  <div className="flex gap-1">
                    <Btn xs icon="documents" onClick={() => setDocsModal(q)}>
                      {t("btn_docs", "Docs")}{q.attachments && q.attachments.length > 0 ? ` (${q.attachments.length})` : ''}
                    </Btn>
                    {['Pending', 'Rejected'].includes(q.status) && <Btn xs icon="edit" onClick={() => openEdit(q)}>{t("btn_edit")}</Btn>}
                    {q.status === 'Pending' && <Btn xs onClick={() => { setSelectedQuotation(q); setTab(1); }}>{t("btn_compare")}</Btn>}
                    {q.status === 'Approved' && <Btn xs onClick={() => setTab(2)}>{t("btn_view_po")}</Btn>}
                    <Btn xs icon="clock" onClick={() => setHistoryModal(q)}>History</Btn>
                    <Btn xs variant="danger" icon="trash" onClick={() => deleteQuotation(q.id)} />
                  </div>
                ];
              })} />
            </div>
            
            {/* Mobile View */}
            <div className="block lg:hidden space-y-3 mt-3">
              {paginatedQuotations.length === 0 && <p className="text-sm text-muted text-center py-8">{t("msg_no_quotations", "No quotations found")}</p>}
              {paginatedQuotations.map((q, i) => {
                const sortedBids = q.bids ? [...q.bids].sort((a, b) => parseFloat(a.amount) - parseFloat(b.amount)) : [];
                const lowestBid = sortedBids.length > 0 ? `₹${parseFloat(sortedBids[0].amount).toLocaleString('en-IN')}` : "-";
                
                let badgeColor = "amber";
                if (q.status === 'Approved') badgeColor = "green";
                else if (q.status === 'Rejected') badgeColor = "red";
                else if (q.status === 'Cancelled') badgeColor = "slate";
                
                let selectedVendor = null;
                if (q.status === 'Approved' && q.purchase_orders && q.purchase_orders.length > 0) {
                  selectedVendor = q.purchase_orders[0].vendor_name;
                }
                
                return (
                  <IonItemSliding key={q.id} className={`rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden ${i === 0 ? 'animate-swipe-hint' : ''}`} style={{ '--background': 'transparent' }}>
                    <IonItem lines="none" style={{ '--background': 'transparent', '--padding-start': '0', '--inner-padding-end': '0' }}>
                      <div className="flex w-full items-start gap-3 p-3 bg-white dark:bg-slate-900">
                        <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex flex-col items-center justify-center flex-shrink-0 mt-1 shadow-sm border border-blue-100">
                          <span className="text-[10px] font-bold uppercase text-blue-400 leading-none">REQ</span>
                          <span className="text-xs font-bold font-mono">{String(q.id).padStart(3, '0')}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold text-ink truncate mb-1">{q.title}</div>
                          <div className="text-xs text-muted mb-1 flex items-center justify-between">
                            <span>{q.created_at ? formatDateByPreference(q.created_at) : ""}</span>
                            {q.due_date && <span className="text-red-400 font-medium">Due: {formatDateByPreference(q.due_date)}</span>}
                          </div>
                          
                          {selectedVendor && (
                            <div className="text-xs font-semibold text-primary mb-1 mt-1 truncate">
                              ✓ {selectedVendor}
                            </div>
                          )}
                          
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <Badge color={badgeColor} dot sm>{q.status}</Badge>
                            <span className="text-[10px] font-medium text-slate-500 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">{(q.bids || []).length}/{q.total_vendors_invited || 1} Responded</span>
                            {lowestBid !== "-" && <span className="text-[10px] font-bold text-ink">Lowest: {lowestBid}</span>}
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setDocsModal(q); }}
                              className="flex items-center gap-1 text-[10px] font-medium text-primary bg-primary/10 hover:bg-primary/20 px-2 py-0.5 rounded transition-colors cursor-pointer"
                            >
                              <Icon name="documents" size={12} />
                              <span>{t("btn_docs", "Docs")}{q.attachments?.length ? ` (${q.attachments.length})` : ''}</span>
                            </button>
                          </div>
                        </div>
                        <div className="text-slate-300 flex flex-col items-center justify-center pt-3 gap-2 ml-1">
                          {q.status === 'Pending' && (
                            <button type="button" onClick={(e) => { e.stopPropagation(); setSelectedQuotation(q); setTab(1); }} className="w-8 h-8 flex items-center justify-center rounded-full bg-primary/10 text-primary">
                              <Icon name="compare" size={14} />
                            </button>
                          )}
                          <Icon name="chevron_left" size={14} />
                        </div>
                      </div>
                    </IonItem>
                    
                    <IonItemOptions side="end">
                      <IonItemOption color="primary" onClick={() => setDocsModal(q)}>
                        <div className="flex flex-col items-center justify-center w-12">
                          <Icon name="documents" size={20} color="white" />
                        </div>
                      </IonItemOption>
                      {['Pending', 'Rejected'].includes(q.status) && (
                        <IonItemOption color="primary" onClick={() => openEdit(q)}>
                          <div className="flex flex-col items-center justify-center w-12">
                            <Icon name="edit" size={20} color="white" />
                          </div>
                        </IonItemOption>
                      )}
                      <IonItemOption color="medium" onClick={() => setHistoryModal(q)}>
                        <div className="flex flex-col items-center justify-center w-12">
                          <Icon name="clock" size={20} color="white" />
                        </div>
                      </IonItemOption>
                      <IonItemOption color="danger" onClick={() => deleteQuotation(q.id)}>
                        <div className="flex flex-col items-center justify-center w-12">
                          <Icon name="trash" size={20} color="white" />
                        </div>
                      </IonItemOption>
                    </IonItemOptions>
                  </IonItemSliding>
                );
              })}
            </div>
            {totalQPages > 1 && (
              <div className="flex items-center justify-between mt-4 border-t border-border pt-4 px-2">
                <span className="text-sm text-muted">Showing {((qPage - 1) * qPerPage) + 1} to {Math.min(qPage * qPerPage, filteredQuotations.length)} of {filteredQuotations.length}</span>
                <div className="flex gap-1">
                  <Btn xs disabled={qPage === 1} onClick={() => setQPage(p => p - 1)}>Prev</Btn>
                  <Btn xs disabled={qPage === totalQPages} onClick={() => setQPage(p => p + 1)}>Next</Btn>
                </div>
              </div>
            )}
          </>
        )}
      </Card>}
      {tab === 1 && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-border shadow-sm flex flex-col gap-4">
            {/* Header: Title and Back Button */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                  <Icon name="compare" size={20} />
                </div>
                <div className="min-w-0">
                  <div className="text-base font-bold text-ink dark:text-white leading-tight">{t("tab_compare_bids")}</div>
                  <div className="text-xs text-muted dark:text-slate-400 font-medium mt-0.5">{t("subtitle_compare_bids")}</div>
                </div>
              </div>

              <div className="flex items-center gap-2 self-start sm:self-auto shrink-0">
                <Btn sm outline onClick={() => setTab(0)} icon="chevron_left">{t("btn_back_to_list")}</Btn>
              </div>
            </div>

            {/* Controls Bar: Switch Request and Actions */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
              <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 w-full lg:w-auto">
                <span className="text-xs font-bold text-slate-400 uppercase shrink-0">{t("label_switch")}</span>
                <select
                  className="bg-slate-50 dark:bg-slate-800 border border-border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20 w-full sm:w-auto sm:min-w-[240px] font-medium transition-all hover:border-primary/30 text-ink dark:text-slate-200"
                  value={activeQuotation?.id || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedQuotation(val ? quotations.find(q => String(q.id) === String(val)) : null);
                  }}
                >
                  <option value="">{t("opt_choose_quotation")}</option>
                  {quotations.filter(q => q.status === 'Pending').map(q => (
                    <option key={q.id} value={q.id}>
                      {q.title}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {activeQuotation && activeQuotation.status === 'Pending' && (
                  <div className="flex flex-wrap gap-2">
                    <Btn sm outline icon="edit" onClick={() => openEdit(activeQuotation)}>{t("btn_edit", "Edit")}</Btn>
                    <Btn sm variant="danger" outline onClick={() => setWorkflowModal({ type: 'Reject', quotation: activeQuotation, comments: "" })}>Reject Request</Btn>
                    <Btn sm variant="danger" outline onClick={() => setWorkflowModal({ type: 'Cancel', quotation: activeQuotation, comments: "" })}>Cancel Request</Btn>
                  </div>
                )}
                {activeQuotation && (
                  <Btn sm outline onClick={() => setDocsModal(activeQuotation)} icon="documents">
                    {t("label_supporting_documents", "Supporting Docs")}{activeQuotation.attachments?.length ? ` (${activeQuotation.attachments.length})` : ''}
                  </Btn>
                )}
                {activeQuotation && <Btn sm outline onClick={() => handleExportComparison(activeQuotation.id)} icon="download">Export</Btn>}
              </div>
            </div>
          </div>

          {activeQuotation ? (
            <div className="fade-up">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {(activeQuotation.bids || []).map((b, i) => {
                  const isRec = !!b.is_lowest;
                  return (
                    <div key={b.id || i} className={`relative rounded-2xl p-6 border-2 transition-all duration-300 ${isRec ? "border-primary bg-primary/5 dark:bg-primary/10 shadow-lg scale-[1.02]" : "border-border bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-700 shadow-sm"}`}>
                      {isRec && (
                        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-md flex items-center gap-1.5 whitespace-nowrap z-10">
                          <Icon name="check" size={10} /> {t("badge_recommended_lowest")}
                        </div>
                      )}

                      <div className="flex justify-between items-start mb-4">
                        <div className={`text-xs font-bold uppercase tracking-wider ${isRec ? "text-primary" : "text-slate-400"}`}>{b.vendor_name}</div>
                        <Badge color={isRec ? "green" : "gray"}>{isRec ? t("badge_best_price") : t("badge_standard")}</Badge>
                      </div>

                      <div className="flex items-baseline gap-1 mb-6">
                        <span className="text-sm font-bold text-ink">₹</span>
                        <span className="text-4xl font-black text-ink">{parseFloat(b.amount).toLocaleString('en-IN')}</span>
                      </div>

                      <div className="space-y-3 mb-8">
                        {[
                          { label: t("label_warranty"), value: b.warranty || t("label_na"), icon: "shield" },
                          { label: "Delivery Time", value: b.timeline || t("label_na"), icon: "clock" },
                          { label: "GST", value: formatGstValue(b.gst) || t("label_na"), icon: "receipt" },
                          { label: "Rating", value: b.rating ? `${b.rating} / 5.0` : t("label_na"), icon: "star" }
                        ].map((item) => (
                          <div key={item.label} className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-800 last:border-0 text-sm">
                            <div className="flex items-center gap-2 text-muted">
                              <Icon name={item.icon} size={14} className="opacity-50" />
                              <span>{item.label}</span>
                            </div>
                            <span className="font-bold text-ink">{item.value}</span>
                          </div>
                        ))}
                      </div>

                      {b.remarks && (
                        <div className="mb-6 p-3 bg-white/50 dark:bg-slate-800/50 rounded-lg border border-slate-100 dark:border-slate-800 text-xs italic text-muted">
                          "{b.remarks}"
                        </div>
                      )}

                      <Btn
                        full
                        variant={isRec ? "primary" : "default"}
                        className="h-11 shadow-sm font-bold"
                        onClick={() => {
                          setWorkflowModal({ type: 'Approve', quotation: activeQuotation, vendor: b, comments: "" });
                        }}
                      >
                        {isRec ? t("btn_approve_generate_po", "Approve & Gen PO") : t("btn_select_bid")}
                      </Btn>
                    </div>
                  );
                })}
                {(activeQuotation.bids || []).length === 0 && (
                  <div className="col-span-full py-20 bg-white dark:bg-slate-900 rounded-2xl border border-dashed border-border flex flex-col items-center text-center">
                    <div className="w-16 h-16 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center text-slate-300 dark:text-slate-600 mb-4">
                      <Icon name="documents" size={32} />
                    </div>
                    <div className="text-ink font-bold">{t("title_no_bids")}</div>
                    <p className="text-muted text-sm mt-1">{t("msg_no_bids")}</p>
                    <Btn sm className="mt-4" onClick={() => openEdit(activeQuotation)}>{t("btn_add_bids_now")}</Btn>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="py-24 bg-white dark:bg-slate-900 rounded-2xl border border-dashed border-border flex flex-col items-center text-center shadow-sm">
              <div className="w-20 h-20 rounded-full bg-primary/5 dark:bg-primary/10 flex items-center justify-center text-primary/30 mb-6">
                <Icon name="compare" size={40} />
              </div>
              <div className="text-xl font-bold text-ink">{t("title_ready_compare")}</div>
              <p className="text-muted text-sm mt-2 max-w-sm">{t("msg_ready_compare")}</p>
            </div>
          )}
        </div>
      )}
      {tab === 2 && <Card>
        <SectionHeader title={t("tab_purchase_orders")} action={<div className="hidden lg:flex items-center gap-2">
          <select value={poFilters.status} onChange={e => setPoFilters(f => ({ ...f, status: e.target.value }))} className="px-3 py-1.5 border border-border rounded-lg text-sm bg-slate-50 dark:bg-slate-800/50 text-slate-800 dark:text-slate-100 outline-none">
            <option className="dark:bg-slate-800" value="All">All Statuses</option>
            <option className="dark:bg-slate-800" value="Draft">Draft</option>
            <option className="dark:bg-slate-800" value="Sent">Sent</option>
            <option className="dark:bg-slate-800" value="Approved">Approved</option>
            <option className="dark:bg-slate-800" value="Completed">Completed</option>
            <option className="dark:bg-slate-800" value="Cancelled">Cancelled</option>
          </select>
          <Btn sm outline onClick={() => handleExportPO(poFilters.status)} icon="download">Export Excel</Btn>
          <Btn sm variant="primary" icon="plus" onClick={() => setModal("po")}>{t("btn_create_po")}</Btn>
        </div>} />
        {loading ? <div className="py-12 text-center text-muted">{t("msg_loading")}</div> : (
          <>
            <div className="hidden lg:block">
              <DataTable cols={[t("col_po_number"), t("label_vendor"), t("placeholder_amount"), t("label_issue_date"), t("col_delivery"), t("label_status"), t("col_actions")]} rows={pos.filter(po => poFilters.status === "All" || po.status === poFilters.status).map(po => {
                let badgeColor = "gray";
                if (po.status === 'Approved') badgeColor = "blue";
                else if (po.status === 'Completed') badgeColor = "green";
                else if (po.status === 'Cancelled') badgeColor = "red";
                else if (po.status === 'Draft' || po.status === 'Sent') badgeColor = "amber";

                const formatDate = (dateString) => {
                  if (!dateString) return "-";
                  return formatDateByPreference(dateString);
                };

                const poNumberCell = (
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{po.po_number}</span>
                    {po.attachments?.length > 0 && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 rounded-md border border-blue-200 dark:border-blue-800" title={`${po.attachments.length} attachment(s)`}>
                        <Icon name="documents" size={10} />
                        {po.attachments.length}
                      </span>
                    )}
                  </div>
                );

                return [
                  poNumberCell,
                  po.vendor_name,
                  `₹${parseFloat(po.amount).toLocaleString('en-IN')}`,
                  formatDate(po.issue_date),
                  formatDate(po.delivery_date),
                  <Badge color={badgeColor} dot>{po.status}</Badge>,
                  <div className="flex gap-1">
                    <Btn xs icon="download" onClick={() => handleDownloadPdf(po)} title="Download PDF" />
                    <Btn xs icon="edit" onClick={() => openEditPO(po)}>{t("btn_edit")}</Btn>
                    <Btn xs icon="eye" onClick={() => setSelectedPO(po)}>{t("btn_view")}</Btn>
                    <Btn xs variant="danger" icon="trash" onClick={() => deletePO(po.id)} />
                  </div>
                ];
              })} />
            </div>

            {/* Mobile View */}
            <div className="block lg:hidden space-y-3 mt-3">
              {pos.length === 0 && <p className="text-sm text-muted text-center py-8">{t("msg_no_pos", "No purchase orders found")}</p>}
              {pos.filter(po => poFilters.status === "All" || po.status === poFilters.status).map((po, i) => {
                let badgeColor = "gray";
                if (po.status === 'Approved') badgeColor = "blue";
                else if (po.status === 'Completed') badgeColor = "green";
                else if (po.status === 'Cancelled') badgeColor = "red";
                else if (po.status === 'Draft' || po.status === 'Sent') badgeColor = "amber";
                
                const formatDate = (dateString) => {
                  if (!dateString) return "-";
                  return formatDateByPreference(dateString);
                };
                
                return (
                  <IonItemSliding key={po.id} className={`rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden ${i === 0 ? 'animate-swipe-hint' : ''}`} style={{ '--background': 'transparent' }}>
                    <IonItem lines="none" style={{ '--background': 'transparent', '--padding-start': '0', '--inner-padding-end': '0' }}>
                      <div className="flex w-full items-start gap-3 p-3 bg-white dark:bg-slate-900">
                        <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex flex-col items-center justify-center flex-shrink-0 mt-1 shadow-sm border border-indigo-100">
                          <span className="text-[10px] font-bold uppercase text-indigo-400 leading-none">PO</span>
                          <span className="text-xs font-bold font-mono">{po.po_number?.split('-').pop() || "-"}</span>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold text-ink truncate mb-1">{po.vendor_name}</div>
                          <div className="text-xs text-muted font-bold text-primary mb-1">₹{parseFloat(po.amount).toLocaleString('en-IN')}</div>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <Badge color={badgeColor} sm>{po.status}</Badge>
                            <span className="text-[10px] text-muted">📅 {formatDate(po.issue_date)}</span>
                            {po.attachments?.length > 0 && (
                              <span className="text-[10px] text-blue-600 dark:text-blue-400 font-medium flex items-center gap-0.5">
                                <Icon name="documents" size={10} /> {po.attachments.length}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="text-slate-300 flex flex-col items-center justify-center pt-3 gap-2 ml-1">
                          <button type="button" onClick={(e) => { e.stopPropagation(); setSelectedPO(po); }} className="w-8 h-8 flex items-center justify-center rounded-full bg-primary/10 text-primary">
                            <Icon name="eye" size={14} />
                          </button>
                          <Icon name="chevron_left" size={14} />
                        </div>
                      </div>
                    </IonItem>
                    
                    <IonItemOptions side="end">
                      <IonItemOption color="primary" onClick={() => handleDownloadPdf(po)}>
                        <div className="flex flex-col items-center justify-center w-12">
                          <Icon name="download" size={20} color="white" />
                        </div>
                      </IonItemOption>
                      <IonItemOption color="secondary" onClick={() => openEditPO(po)}>
                        <div className="flex flex-col items-center justify-center w-12">
                          <Icon name="edit" size={20} color="white" />
                        </div>
                      </IonItemOption>
                      <IonItemOption color="danger" onClick={() => deletePO(po.id)}>
                        <div className="flex flex-col items-center justify-center w-12">
                          <Icon name="trash" size={20} color="white" />
                        </div>
                      </IonItemOption>
                    </IonItemOptions>
                  </IonItemSliding>
                );
              })}
            </div>
          </>
        )}
      </Card>}

      {tab === 3 && (
        <Card>
          <SectionHeader title="Reports" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-4">
            <div className="border border-slate-200 dark:border-slate-800 rounded-xl p-6 bg-slate-50 dark:bg-slate-900/50 flex flex-col items-center text-center relative">
              <div className="w-16 h-16 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mb-4">
                <Icon name="documents" size={32} />
              </div>
              <h3 className="text-lg font-bold text-ink">Quotation Status Report</h3>
              <p className="text-muted text-sm mt-2 mb-4">Export all quotation requests and their statuses, including vendor counts and lowest bids.</p>
              
              <div className="w-full mb-4 flex flex-col items-start text-left">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Filter by Status</label>
                <select value={reportQStatus} onChange={e => setReportQStatus(e.target.value)} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 outline-none">
                  <option value="All">All Statuses</option>
                  <option value="Pending">Pending</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </div>

              <Btn onClick={() => handleExportQuotations(reportQStatus)} variant="primary" icon="download" full>Download Excel</Btn>
            </div>

            <div className="border border-slate-200 dark:border-slate-800 rounded-xl p-6 bg-slate-50 dark:bg-slate-900/50 flex flex-col items-center text-center relative">
              <div className="w-16 h-16 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center mb-4">
                <span className="text-sm font-bold uppercase">PO</span>
              </div>
              <h3 className="text-lg font-bold text-ink">Purchase Order Report</h3>
              <p className="text-muted text-sm mt-2 mb-4">Export all generated purchase orders, amounts, expected delivery dates, and current statuses.</p>
              
              <div className="w-full mb-4 flex flex-col items-start text-left">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Filter by Status</label>
                <select value={reportPOStatus} onChange={e => setReportPOStatus(e.target.value)} className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 outline-none">
                  <option value="All">All Statuses</option>
                  <option value="Draft">Draft</option>
                  <option value="Sent">Sent</option>
                  <option value="Approved">Approved</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>

              <Btn onClick={() => handleExportPO(reportPOStatus)} variant="primary" icon="download" full>Download Excel</Btn>
            </div>
            
            <div className="border border-slate-200 dark:border-slate-800 rounded-xl p-6 bg-slate-50 dark:bg-slate-900/50 flex flex-col items-center text-center relative md:col-span-2 lg:col-span-1">
              <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-4">
                <Icon name="clock" size={32} />
              </div>
              <h3 className="text-lg font-bold text-ink">Audit History Report</h3>
              <p className="text-muted text-sm mt-2 mb-4">Export a comprehensive log of all quotation approvals, rejections, cancellations, and comments.</p>
              
              <div className="flex-1"></div>

              <Btn onClick={() => handleExportAuditHistory()} variant="primary" icon="download" full>Download Excel</Btn>
            </div>
          </div>
        </Card>
      )}
      
      {/* FAB for Mobile */}
      {!modal && !docsModal && !selectedQuotation && !selectedPO && !historyModal && !workflowModal && createPortal(
        <div className="fixed bottom-6 right-6 z-50 lg:hidden pointer-events-auto">
          <button
            onClick={() => { tab === 0 ? openNewQuotationModal() : setModal("po"); }}
            className={`w-14 h-14 bg-primary text-white rounded-full flex items-center justify-center shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:scale-105 transition-transform active:scale-95 border-0 cursor-pointer ${tab === 1 ? 'hidden' : ''}`}
          >
            <Icon name="plus" size={24} />
          </button>
        </div>,
        document.body
      )}
      
      {workflowModal && (
        <Modal title={`${workflowModal.type} Quotation`} onClose={() => setWorkflowModal(null)}>
          <div className="p-2">
            <p className="text-sm text-muted mb-4">
              {workflowModal.type === 'Approve' ? `You are approving the bid from ${workflowModal.vendor?.vendor_name}.` : `You are about to ${workflowModal.type.toLowerCase()} this quotation request.`}
              <br/>Please provide any comments or reasons below:
            </p>
            <FormRow>
              <Field label="Comments (Optional)" type="textarea" rows={3} value={workflowModal.comments} onChange={e => setWorkflowModal(f => ({ ...f, comments: e.target.value }))} />
            </FormRow>
            <FormActions 
              onSave={submitWorkflow} 
              onCancel={() => setWorkflowModal(null)} 
              label={`Confirm ${workflowModal.type}`} 
              loading={saving} 
            />
          </div>
        </Modal>
      )}

      {historyModal && (
        <Modal title="Approval History" onClose={() => setHistoryModal(null)}>
          <div className="p-4 space-y-4">
            {(!historyModal.approval_history || historyModal.approval_history.length === 0) ? (
              <div className="text-center text-muted py-4">No history available for this quotation.</div>
            ) : (
              <div className="relative border-l border-slate-200 dark:border-slate-700 ml-3 space-y-6 pb-4 mt-2">
                {historyModal.approval_history.map((log, idx) => {
                  let color = "bg-amber-500";
                  if (log.status === 'Approved') color = "bg-green-500";
                  else if (log.status === 'Rejected') color = "bg-red-500";
                  else if (log.status === 'Cancelled') color = "bg-slate-500";
                  
                  return (
                    <div key={idx} className="relative pl-6">
                      <div className={`absolute -left-1.5 top-1.5 w-3 h-3 rounded-full ${color} border-2 border-white dark:border-slate-900`}></div>
                      <div className="text-sm font-bold text-ink">{log.status}</div>
                      <div className="text-xs text-muted mb-1">{log.user_name} • {formatDateByPreference(log.timestamp)}</div>
                      {log.comments && <div className="text-sm text-slate-700 dark:text-slate-300 italic bg-slate-50 dark:bg-slate-800 p-2 rounded mt-1">"{log.comments}"</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
